"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useReducedMotion } from "@/lib/motion";
import { usePrefersLight } from "@/hooks/use-color-scheme";
import { canvasPalette, canvasRgba } from "@/components/sections/home/stats/data";
import { getGlobeDots, mulberry32, type GlobeDot, type GlobeDotKind } from "@/lib/globe-dots";
import { lonLatToUnit } from "@/lib/globe-borders";

/* Auto-spinning particle Earth for the Market Research card (Canvas2D, no WebGL
   dependency). Country polygons from the shared world atlas are rasterised once
   into an offscreen land mask; particles are then sampled on land only, so the
   dome reads as real continents instead of random noise. A sparse, faint ocean
   layer keeps the sphere silhouette, and beacon rings pulse on major hub cities.
   Colours come from the shared theme-aware canvas palette (canvases can't read
   CSS vars): ink dots with deep green/amber accents in light, white dots with
   mint/yellow accents in dark. */

const ROT_SPEED = 0.0032; // radians / frame — one revolution ≈ 33s
const HEIGHT = 200;

type Rgb = [number, number, number];
type Kind = GlobeDotKind;
type Dot = GlobeDot;

interface Beacon {
  ux: number;
  uy: number;
  uz: number;
  kind: Kind;
  phase: number;
}

interface Geometry {
  land: Dot[];
  ocean: Dot[];
  beacons: Beacon[];
}

// Hub cities for the beacon rings — [lon, lat], northern band so they stay in frame.
const HUBS: [number, number][] = [
  [-118.2, 34.0], // Los Angeles
  [-87.6, 41.9], // Chicago
  [-74.0, 40.7], // New York
  [-79.4, 43.7], // Toronto
  [-99.1, 19.4], // Mexico City
  [-0.1, 51.5], // London
  [2.3, 48.9], // Paris
  [13.4, 52.5], // Berlin
  [31.2, 30.0], // Cairo
  [28.9, 41.0], // Istanbul
  [55.3, 25.2], // Dubai
  [72.9, 19.1], // Mumbai
  [126.9, 37.5], // Seoul
  [139.7, 35.7], // Tokyo
];

let geomCache: Geometry | null = null;

/** Land/ocean dots come from the shared dot-sphere module (also used by the Global
 *  Network globe); the beacons are card-specific, so they stay here. */
function getGeometry(): Geometry {
  if (geomCache) return geomCache;
  const { land, ocean } = getGlobeDots();
  const rand = mulberry32(90210);
  const beacons: Beacon[] = HUBS.map(([lon, lat], i) => {
    const u = lonLatToUnit(lon, lat);
    return { ux: u.ux, uy: u.uy, uz: u.uz, kind: i % 3 === 2 ? 2 : 1, phase: rand() * Math.PI * 2 };
  });
  geomCache = { land, ocean, beacons };
  return geomCache;
}

export default function MarketGlobe() {
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef(0);
  const reduced = useReducedMotion();
  const prefersLight = usePrefersLight();
  const [width, setWidth] = useState(360);

  const pal = useMemo(() => canvasPalette(prefersLight), [prefersLight]);
  const rgba = useMemo(() => canvasRgba(prefersLight), [prefersLight]);
  const geom = useMemo(() => (typeof document === "undefined" ? null : getGeometry()), []);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      setWidth(Math.max(Math.floor(entry.contentRect.width), 1));
    });
    ro.observe(el);
    setWidth(el.offsetWidth || 360);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap || !geom) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const W = width;
    const H = HEIGHT;
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    ctx.scale(dpr, dpr);

    const cx = W / 2;
    const cy = H * 1.04; // centre just below the frame — the northern dome fills it
    const rGlobe = Math.min(W * 0.58, H * 1.05);
    // Base land/ocean dots: a mid gray in light (the near-black foreground read
    // too heavy), soft white in dark. Green/yellow accents are unchanged.
    const base: Rgb = prefersLight ? [158, 165, 178] : pal.white;
    const colors: Rgb[] = [base, pal.grn, pal.yel];
    let rot = 0;
    let t = 0;

    const drawLayer = (layer: Dot[]) => {
      const cr = Math.cos(rot);
      const sr = Math.sin(rot);
      // Whole sphere is drawn (no back-face culling) so the far-side dots show
      // through for depth. Two passes keep the near face on top of the see-
      // through far face: far hemisphere first (faint + small), then the near.
      const paint = (nearPass: boolean) => {
        for (const d of layer) {
          const rz = d.ux * sr + d.uz * cr;
          if (nearPass ? rz < 0 : rz >= 0) continue;
          const sy = cy - rGlobe * d.uy;
          if (sy < -3 || sy > H + 3) continue;
          const sx = cx + rGlobe * (d.ux * cr - d.uz * sr);
          const front = (rz + 1) / 2; // 0 far .. 1 near
          const d3 = Math.pow(front, 1.5);
          // Near face bright + large; far face stays visible but faint + smaller,
          // so the globe reads as a translucent 3D sphere with real depth.
          const depth = 0.28 + d3 * 0.72;
          ctx.fillStyle = rgba(colors[d.kind], d.alpha * depth);
          ctx.beginPath();
          ctx.arc(sx, sy, d.size * (0.62 + d3 * 1.08), 0, Math.PI * 2);
          ctx.fill();
        }
      };
      paint(false); // far hemisphere
      paint(true); // near hemisphere on top
    };

    const draw = () => {
      const cr = Math.cos(rot);
      const sr = Math.sin(rot);
      ctx.clearRect(0, 0, W, H);
      drawLayer(geom.ocean);
      drawLayer(geom.land);

      for (const b of geom.beacons) {
        const rz = b.ux * sr + b.uz * cr;
        if (rz < 0.12) continue; // beacons only on the front face
        const sy = cy - rGlobe * b.uy;
        if (sy < 4 || sy > H - 4) continue;
        const sx = cx + rGlobe * (b.ux * cr - b.uz * sr);
        const pulse = reduced ? 0.4 : 0.5 + 0.5 * Math.sin(t * 0.04 + b.phase);
        const col = colors[b.kind];

        ctx.fillStyle = rgba(col, 0.9);
        ctx.beginPath();
        ctx.arc(sx, sy, 3.0, 0, Math.PI * 2);
        ctx.fill();
        // crisp pulsing ring — no blurry glow
        ctx.strokeStyle = rgba(col, 0.4 * (1 - pulse) + 0.06);
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(sx, sy, 4 + pulse * 4, 0, Math.PI * 2);
        ctx.stroke();
      }
    };

    if (reduced) {
      draw();
      return;
    }

    let running = false;
    const loop = () => {
      rafRef.current = requestAnimationFrame(loop);
      rot += ROT_SPEED;
      t += 1;
      draw();
    };
    const start = () => {
      if (running) return;
      running = true;
      loop();
    };
    const stop = () => {
      running = false;
      cancelAnimationFrame(rafRef.current);
    };

    // Pause the animation while the card is offscreen.
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) start();
      else stop();
    });
    io.observe(wrap);

    return () => {
      io.disconnect();
      stop();
    };
  }, [width, geom, rgba, pal, prefersLight, reduced]);

  return (
    <div ref={wrapRef} className="pointer-events-none relative w-full" style={{ height: HEIGHT }}>
      <canvas
        ref={canvasRef}
        style={{ width, height: HEIGHT, display: "block" }}
        aria-hidden="true"
      />
      {/* soft fade into the card edge so the crop never reads as a hard cut */}
      <div
        aria-hidden="true"
        className="absolute inset-x-0 bottom-0 h-8"
        style={{ background: "linear-gradient(to top, var(--background), transparent)" }}
      />
    </div>
  );
}
